/**
 * Disconnect Classifier
 *
 * IMPORTANT: This file is duplicated in apps/marketing-demo/src/features/quickstart/utils/disconnectClassifier.ts.
 * Both copies must stay in sync. If you change one, change the other.
 */

/**
 * Legacy host-initiated reasons that older host builds still emit WITHOUT
 * the `host:` prefix. Each entry has been since prefixed at the source —
 * this set is purely for backwards compatibility with hosts that haven't
 * been updated to a build containing the prefix.
 */
const LEGACY_HOST_INITIATED_REASONS: ReadonlySet<string> = new Set([
  'game_closed',
  'peer_connection_timeout',
  'duplicate_peer',
  'host_app_closed',
  'all_players_left_restart',
  'all_players_left_no_restart',
  'campaign_clear:active_match',
  'pavilion:no_players_timeout',
  'init:timeout',
  'invitation:invalidated',
  'ipc:stopstream',
  'coordinator_requested',
]);

export const isDisconnectRetryable = (kickReason?: string): boolean => {
  // No reason at all almost always means the host went away (refresh,
  // crash, intentional close). Don't reconnect — the peer-level retry
  // inside the WebRTC layer already handled transient drops.
  if (!kickReason) return false;

  const reason = kickReason.toLowerCase();

  // --- Non-retryable: host-initiated session end --------------------------

  // Future canonical form. Every host-initiated peer.close() should use this.
  if (reason.startsWith('host:')) return false;

  // Backwards-compat: hosts on older builds that emit unprefixed reasons.
  if (LEGACY_HOST_INITIATED_REASONS.has(reason)) return false;

  // Ad-mode session ending naturally (player died, game ended, etc.).
  if (reason.includes('ad_mode:')) return false;

  // --- Non-retryable: intentional disconnects from the player side --------

  if (reason.includes('quickstart:')) return false;
  if (reason.includes('player:close')) return false;

  // --- Non-retryable: explicit kicks / refusals / invalid sessions --------

  if (reason.includes('kicked')) return false;
  if (reason.includes('refused')) return false;
  // ICE credential failures (ice_invalid_credentials) are reclaimable — do not
  // treat every substring "invalid" as a non-retryable session end.
  const iceKey = reason.replace(/-/g, '_');
  if (reason.includes('invalid') && !iceKey.startsWith('ice_')) return false;

  // --- Retryable: transient WebRTC / network problems ---------------------

  if (reason.includes('transport_disconnected')) return true;
  if (
    reason.includes('ice_disconnected') ||
    reason.includes('ice_failed') ||
    reason.includes('ice_timeout') ||
    reason.startsWith('ice_') ||
    reason === 'ice'
  ) {
    return true;
  }
  if (reason.includes('network')) return true;
  if (reason.includes('timeout')) return true;
  if (reason.includes('connection_lost')) return true;

  // Default: unknown reason. Treat as host-initiated and don't reconnect.
  // This is the conservative choice — see file-level rule (1).
  return false;
};
